import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, FileSpreadsheet } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Modal } from '../../componentes/sobreposicao';
import { get } from '../../nucleo/api';
import { data, hojeIso, mesAno, nomePessoa } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { COR_CATEGORIA, COR_ESTADO, type ItemCalendario, type LinhaFolha } from './tipos';

const somarMes = (mes: string, n: number) => {
  const [a, m] = mes.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(a, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const ultimoDia = (mes: string) => {
  const [a, m] = mes.split('-').map(Number) as [number, number];
  return `${mes}-${String(new Date(Date.UTC(a, m, 0)).getUTCDate()).padStart(2, '0')}`;
};

/** Calendário da equipe: quem está ausente (ou pediu) no mês, por pessoa, e a exportação da competência para a folha. */
export function Calendario() {
  const { t } = useTranslation('ausencias');
  const [mes, setMes] = useState(() => hojeIso().slice(0, 7));
  const [folha, setFolha] = useState(false);
  // Val5 (29/10): exportar a competência para a folha tem ação própria (`ausencias.exportar`).
  const podeExportar = useSessao().pode('ausencias.exportar');
  const q = useQuery({
    queryKey: ['ausencias', 'calendario', mes],
    queryFn: () => get<{ de: string; ate: string; itens: ItemCalendario[] }>('/ausencias/calendario', { de: `${mes}-01`, ate: ultimoDia(mes) }),
  });
  const porPessoa = new Map<string, { nome: string; itens: ItemCalendario[] }>();
  for (const i of q.data?.itens ?? []) {
    const g = porPessoa.get(i.funcionario_id) ?? { nome: i.funcionario_nome ?? '', itens: [] };
    g.itens.push(i);
    porPessoa.set(i.funcionario_id, g);
  }
  return (
    <>
      {podeExportar && (
        <AcaoPrimaria>
          <Botao icone={<FileSpreadsheet className="size-4" />} onClick={() => setFolha(true)}>
            {t('acoes.folhaDoMes')}
          </Botao>
        </AcaoPrimaria>
      )}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Botao variante="fantasma" icone={<ChevronLeft className="size-4" />} aria-label={t('acoes.mesAnterior')} title={t('acoes.mesAnterior')} onClick={() => setMes((m) => somarMes(m, -1))} />
        <span className="min-w-32 text-center font-medium text-tinta-1" aria-live="polite">
          {mesAno(mes)}
        </span>
        <Botao variante="fantasma" icone={<ChevronRight className="size-4" />} aria-label={t('acoes.mesSeguinte')} title={t('acoes.mesSeguinte')} onClick={() => setMes((m) => somarMes(m, 1))} />
      </div>
      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      ) : porPessoa.size === 0 ? (
        <Vazio texto={t('vazio.calendario')} />
      ) : (
        <div className="grid min-w-0 gap-3 md:grid-cols-2">
          {[...porPessoa.entries()].map(([id, g]) => (
            <Cartao key={id} titulo={nomePessoa(g.nome) || '-'}>
              <ul className="flex flex-col gap-2">
                {g.itens.map((i) => (
                  <li key={i.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span className="flex min-w-0 flex-wrap items-center gap-2">
                      <Selo cor={COR_CATEGORIA[i.categoria]}>{i.tipo_nome}</Selo>
                      <span className="text-tinta-2">{t('periodo', { de: data(i.inicio), ate: data(i.fim) })}</span>
                    </span>
                    <Selo cor={COR_ESTADO[i.estado]}>{t(`estado.${i.estado}`)}</Selo>
                  </li>
                ))}
              </ul>
            </Cartao>
          ))}
        </div>
      )}
      {folha && podeExportar && <FolhaDoMes mes={mes} aoFechar={() => setFolha(false)} />}
    </>
  );
}

export function FolhaDoMes({ mes, aoFechar }: { mes: string; aoFechar: () => void }) {
  const { t } = useTranslation('ausencias');
  const { t: tn } = useTranslation();
  const q = useQuery({ queryKey: ['ausencias', 'folha', mes], queryFn: () => get<{ ano_mes: string; itens: LinhaFolha[] }>('/ausencias/folha', { ano_mes: mes }) });
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={`${t('acoes.folhaDoMes')} · ${mesAno(mes)}`}
      rodape={
        <Botao variante="fantasma" onClick={aoFechar}>
          {tn('comum.fechar')}
        </Botao>
      }
    >
      <p className="mb-3 text-sm text-tinta-3">{t('aviso.folha')}</p>
      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      ) : q.data.itens.length === 0 ? (
        <Vazio texto={t('vazio.folha')} />
      ) : (
        <ul className="flex flex-col divide-y divide-borda">
          {q.data.itens.map((l) => (
            <li key={`${l.funcionario_id}:${l.tipo_id}`} className="flex flex-wrap items-start justify-between gap-2 py-2 text-sm">
              <span className="min-w-0">
                <span className="block font-medium text-tinta-1">{nomePessoa(l.funcionario_nome) || '-'}</span>
                <span className="block text-tinta-3">{l.periodos.map((p) => { const [de, ate] = p.split(' a '); return t('periodo', { de: data(de), ate: data(ate) }); }).join(', ')}</span>
              </span>
              <span className="flex flex-wrap items-center gap-2">
                <Selo cor={l.remunerada ? 'verde' : 'laranja'}>{l.remunerada ? t('remunerada') : t('naoRemunerada')}</Selo>
                <span className="text-tinta-2">{l.tipo_nome}</span>
                <span className="font-mono text-tinta-1">{t('diasN', { count: l.dias })}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
